/**
 * Relative-time helper — "3 hours ago", "yesterday", "in 2 days", etc.
 * Uses the browser's Intl.RelativeTimeFormat when available for i18n.
 * Falls back to a compact English string on older engines.
 */

const DIVISIONS = [
  { amount: 60, name: "seconds" },
  { amount: 60, name: "minutes" },
  { amount: 24, name: "hours" },
  { amount: 7, name: "days" },
  { amount: 4.34524, name: "weeks" },
  { amount: 12, name: "months" },
  { amount: Number.POSITIVE_INFINITY, name: "years" },
];

function _fallback(diffSec) {
  const abs = Math.abs(diffSec);
  if (abs < 60) return diffSec < 0 ? "just now" : "just now";
  if (abs < 3600) return `${Math.round(abs / 60)}m ago`;
  if (abs < 86400) return `${Math.round(abs / 3600)}h ago`;
  if (abs < 604800) return `${Math.round(abs / 86400)}d ago`;
  return `${Math.round(abs / 604800)}w ago`;
}

/**
 * @param {string|Date|number|null|undefined} value — ISO string, Date, ms epoch
 * @param {object} [opts]
 * @param {string} [opts.fallback="—"] — what to return when value is falsy
 * @returns {string}
 */
export function timeAgo(value, opts = {}) {
  const fallback = opts.fallback ?? "—";
  if (!value) return fallback;
  const then =
    value instanceof Date ? value : new Date(value);
  if (Number.isNaN(then.getTime())) return fallback;

  const diffSec = (then.getTime() - Date.now()) / 1000;
  const rtf =
    typeof Intl !== "undefined" && Intl.RelativeTimeFormat
      ? new Intl.RelativeTimeFormat(undefined, { numeric: "auto" })
      : null;
  if (!rtf) return _fallback(diffSec);

  let duration = diffSec;
  for (let i = 0; i < DIVISIONS.length; i++) {
    const div = DIVISIONS[i];
    if (Math.abs(duration) < div.amount) {
      return rtf.format(Math.round(duration), div.name);
    }
    duration /= div.amount;
  }
  return _fallback(diffSec);
}

/**
 * Absolute date/time string for tooltip use.
 * @param {string|Date|number|null|undefined} value
 * @returns {string}
 */
export function formatAbsolute(value) {
  if (!value) return "";
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}
